import Utils from "../../../../../../../../core/services/utils/utils";
import {PreventionInvalidityCode} from "../preventative-interactions";
import {BaseImportTransformer, toPartNumberUpper} from "./shared";

/**
 * Part visibility import — phased validation (see {@link BaseImportTransformer} run contract).
 * 1) Row / cell checks (missing part number)
 * 2) Batched part id resolution (cached via context)
 * 3) Unknown part
 * 4) Boolean fields (`isVisible`, `isVisibleForClient`) — evaluated for every row with an item
 *
 * Returns `apiRequests` as a single batch `{ models: [...] }` matching the legacy API contract.
 */
export class PartVisibilityImportTransformer extends BaseImportTransformer {
    /**
     * @param {Array<Object|null|undefined>} mappedData
     * @param {boolean} isFinalRun
     */
    async run(mappedData, isFinalRun = false) {
        const rows = mappedData ?? [];
        const preventativeIssues = [];

        /** @type {{index: number, item: *, partNoUpper: string, codes: string[]}[]} */
        const rowState = rows.map((item, index) => ({
            index,
            item,
            partNoUpper: toPartNumberUpper(item?.partNumber),
            codes: [],
        }));

        // ── Phase 1: preliminary (CSV-only) ─────────────────────────────────────
        for (const rs of rowState) {
            if (!rs.item) continue;
            if (!rs.partNoUpper) {
                rs.codes.push(PreventionInvalidityCode.INVALID_PART);
            }
        }

        // ── Phase 2–3: part ids (cache + unknown part) ───────────────────────────
        const uniqueParts = [...new Set(rowState.map((r) => r.partNoUpper).filter(Boolean))].sort();
        const partIds = uniqueParts.length
            ? (await this.ctx.fetchPartInfoByPartNumbers(uniqueParts)).partIds
            : {};

        for (const rs of rowState) {
            if (!rs.item) continue;
            if (rs.partNoUpper && !partIds[rs.partNoUpper]) {
                rs.codes.push(PreventionInvalidityCode.INVALID_PART);
            }
        }

        // ── Phase 4: visibility booleans ─────────────────────────────────────────
        for (const rs of rowState) {
            if (!rs.item) continue;
            const isVisible = Utils.tryConvertStringToBoolean(rs.item.isVisible);
            const isVisibleForClient = Utils.tryConvertStringToBoolean(rs.item.isVisibleForClient);
            // since either is optional but at least one should exist
            if (isVisible === undefined && isVisibleForClient === undefined) {
                rs.codes.push(PreventionInvalidityCode.INVALID_BOOLEAN);
            }
        }

        // ── Emit preventative issues ─────────────────────────────────────────────
        for (const rs of rowState) {
            if (!rs.item) {
                preventativeIssues.push({
                    rowIndex: rs.index,
                    originalRow: rows[rs.index],
                    invalidityCodes: [PreventionInvalidityCode.INVALID_PART],
                });
                continue;
            }
            if (!rs.codes.length) continue;
            preventativeIssues.push({
                rowIndex: rs.index,
                originalRow: rs.item,
                invalidityCodes: [...new Set(rs.codes)],
            });
        }

        const issueIndexSet = new Set(preventativeIssues.map((p) => p.rowIndex));

        const processedEntries = rows.map((item, index) => {
            if (!item) {
                return isFinalRun ? null : {partId: null, partNumber: ''};
            }

            const rs = rowState[index];
            if (!rs.partNoUpper) {
                return isFinalRun ? null : {partId: null, partNumber: ''};
            }

            const partId = partIds[rs.partNoUpper];
            const isVisible = Utils.tryConvertStringToBoolean(item.isVisible);
            const isVisibleForClient = Utils.tryConvertStringToBoolean(item.isVisibleForClient);
            const hasIssue = issueIndexSet.has(index);

            if (hasIssue) {
                return isFinalRun
                    ? null
                    : {
                        partNumber: toPartNumberUpper(item?.partNumber),
                        partId: partId ?? null,
                        isVisible: isVisible ?? null,
                        isVisibleForClient: isVisibleForClient ?? null,
                    };
            }

            return {
                partNumber: toPartNumberUpper(item?.partNumber),
                partId: partId,
                isVisible: isVisible,
                isVisibleForClient: isVisibleForClient,
            };
        });

        const batchSize = processedEntries.length;
        const apiRequests = [];
        for (let i = 0; i < processedEntries.length; i += batchSize) {
            let batch = processedEntries.slice(i, i + batchSize);
            if (isFinalRun) {
                batch = batch.filter((b) => b !== null);
            }
            if (batch.length === 0) continue;
            apiRequests.push({models: batch});
        }

        return {apiRequests, preventativeIssues};
    }
}
